import type { AutocompleteFieldProps } from '@workramp/ui';
import type { ContentProviderSearchResult } from '~/features/content-search';
import type { APP_CONSTANTS } from '~/common/explicit-exports';
import { AutocompleteField, Icon, P, Stack } from '@workramp/ui';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { useDebouncedValue } from '~/hooks';
import { useDurationFormatter } from '~/features/temporal';
import { useGetContentProviderContentSearchResultsQuery } from '~/features/content-search';

interface ContentProviderContentSearchFieldProps
  extends Omit<AutocompleteFieldProps<ContentProviderSearchResult>, 'children' | 'items'> {
  provider: (typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES)['UDEMY' | 'LINKEDIN_LEARNING'];
  onContentSelection: (contentId: string) => void;
}

export const ContentProviderContentSearchField = ({
  provider,
  onContentSelection,
  ...props
}: ContentProviderContentSearchFieldProps) => {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query);

  const { data } = useGetContentProviderContentSearchResultsQuery(provider, debouncedQuery, {
    enabled: !!debouncedQuery && debouncedQuery.length > 2,
  });

  const items = Array.isArray(data?.results) ? data.results.slice(0, 10) : [];

  const durationFormatter = useDurationFormatter({ short: true, round: true, units: ['h', 'm'], delimiter: ' ' });

  const NO_RESULTS_FOUND: ContentProviderSearchResult = {
    asset_id: 'no-result-found',
    title: 'No results found',
    description: null,
    link: null,
    image: null,
    duration_seconds: null,
  };

  return (
    <AutocompleteField
      inputValue={query}
      onInputChange={setQuery}
      onSelectionChange={onContentSelection}
      items={items.length > 0 ? items : [NO_RESULTS_FOUND]}
      disabledKeys={[NO_RESULTS_FOUND.asset_id]}
      leadingIconName="search:regular"
      {...props}
    >
      {(item) => (
        <AutocompleteField.Item key={item.asset_id} textValue={item.title}>
          <Stack size={1}>
            <P weight="medium" size={1}>
              {item.title}
            </P>
            {item.duration_seconds && (
              <P size={0}>
                <Icon name="clock:regular" size="sm" aria-hidden /> {durationFormatter(item.duration_seconds * 1000)}
              </P>
            )}
            {item.description && (
              <P size={1} maxLines={1} color={theme.colors.on[600]}>
                {item.description}
              </P>
            )}
          </Stack>
        </AutocompleteField.Item>
      )}
    </AutocompleteField>
  );
};
